import { Card, Col, Empty, Row, Tag, theme as antdTheme } from 'antd';
import {
  AlertOutlined,
  BookOutlined,
  CalendarOutlined,
  DollarOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { MaskedAmount } from '@/shared/components/masked-amount';
import { useSchoolTheme } from '@/themes/use-theme';
import type { DashboardSummary } from '@/api/dashboard';
import {
  DashboardHeader, KpiGrid, PKR, RecentNoticesCard, SignalRow,
  type KpiSpec,
} from './shared';
import { PendingApprovalsCard } from './pending-approvals';

/**
 * Default dashboard for roles with school-wide oversight: Super Admin and
 * Principal. Shows the full set of KPIs, enrollment chart, operational
 * signals, and recent notices. Financial figures are masked behind the eye
 * toggle (both roles have the reveal capability).
 */
export function AdminDashboard({ data }: { data: DashboardSummary }) {
  const theme = useSchoolTheme();
  const { token } = antdTheme.useToken();

  const attTotal = data.studentAttendanceToday.expected;
  const attPresentPct = attTotal === 0
    ? 0
    : Math.round((data.studentAttendanceToday.present / attTotal) * 1000) / 10;

  const kpis: KpiSpec[] = [
    {
      key: 'students',
      label: 'Active students',
      value: data.students.active,
      hint: `${data.students.total.toLocaleString()} on roll · ${data.students.newEnrolledThisMonth} new this month`,
      icon: <UserOutlined />,
      tone: 'primary',
    },
    {
      key: 'attendance',
      label: "Today's attendance",
      value: attTotal === 0 ? '—' : `${attPresentPct}%`,
      hint: attTotal === 0
        ? 'No attendance recorded today'
        : `${data.studentAttendanceToday.present.toLocaleString()} present of ${attTotal.toLocaleString()}`,
      icon: <CalendarOutlined />,
      tone: 'info',
    },
    {
      key: 'fees-today',
      label: 'Fees collected today',
      value: PKR(Number(data.fees.collectedToday)),
      hint: (
        <>
          <MaskedAmount value={PKR(Number(data.fees.collectedThisMonth))} maskWidth={5} /> this month
        </>
      ),
      sensitive: true,
      icon: <DollarOutlined />,
      tone: 'success',
    },
    {
      key: 'defaulters',
      label: 'Outstanding dues',
      value: PKR(Number(data.fees.outstanding)),
      hint: `${data.fees.defaulterCount.toLocaleString()} defaulters`,
      sensitive: true,
      icon: <AlertOutlined />,
      tone: 'warning',
    },
  ];

  const breakdown = data.classBreakdown.map((row) => ({
    name: `${row.className.replace(/^Grade /, '')}-${row.sectionName}`,
    students: row.studentCount,
  }));
  const totalEnrolled = breakdown.reduce((acc, r) => acc + r.students, 0);
  const [c1, c2, c3] = theme.chartColors;
  const palette = [c1, c2, c3, theme.chartColors[3] ?? c1, theme.chartColors[4] ?? c2];

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <DashboardHeader role={data.role} asOf={data.asOf} />
      <KpiGrid kpis={kpis} />

      <Row gutter={[16, 16]}>
        <Col xs={24} xl={16}>
          <Card
            title="Enrollment by class (current year)"
            extra={<Tag color="processing">{totalEnrolled.toLocaleString()} enrolled</Tag>}
            style={{ height: '100%' }}
          >
            {breakdown.length === 0 ? (
              <Empty description="No class-sections defined for the current academic year" />
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={breakdown} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid stroke={token.colorBorderSecondary} strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" stroke={token.colorTextTertiary} fontSize={12} />
                  <YAxis stroke={token.colorTextTertiary} fontSize={12} allowDecimals={false} />
                  <Tooltip
                    cursor={{ fill: token.colorFillQuaternary }}
                    contentStyle={{
                      background: token.colorBgElevated,
                      border: `1px solid ${token.colorBorderSecondary}`,
                      borderRadius: 8,
                    }}
                  />
                  <Bar dataKey="students" radius={[6, 6, 0, 0]}>
                    {breakdown.map((_, i) => <Cell key={i} fill={palette[i % palette.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Col>

        <Col xs={24} xl={8}>
          <Card title="Operational signals" style={{ height: '100%' }}>
            <SignalRow icon={<TeamOutlined />} label="Active staff" value={`${data.staff.active.toLocaleString()} / ${data.staff.total.toLocaleString()}`} />
            <SignalRow icon={<BookOutlined />} label="Active classes" value={data.academics.activeClasses.toString()} />
            <SignalRow icon={<BookOutlined />} label="Active subjects" value={data.academics.activeSubjects.toString()} />
            <SignalRow icon={<CalendarOutlined />} label="Exams in 7 days" value={data.academics.upcomingExamsThisWeek.toString()} />
            <SignalRow icon={<AlertOutlined />} label="Open complaints" value={data.frontDesk.openComplaints.toString()} />
            <SignalRow icon={<UserOutlined />} label="Visitors today" value={data.frontDesk.visitorsToday.toString()} />
            <SignalRow icon={<UserOutlined />} label="Inquiries this month" value={data.frontDesk.inquiriesThisMonth.toString()} />
            <SignalRow icon={<AlertOutlined />} label="Low-stock items" value={data.inventory.lowStockCount.toString()} />
          </Card>
        </Col>
      </Row>

      <PendingApprovalsCard />

      <RecentNoticesCard notices={data.recentNotices} />
    </div>
  );
}
